import { api } from "./client";
import type { FileDTO, FolderDTO, SortField, SortOrder, User } from "@titancloud/shared";

export async function listUsers(): Promise<User[]> {
  return api.get("/admin/users");
}

export async function getUser(userId: string): Promise<User> {
  return api.get(`/admin/users/${userId}`);
}

export async function updateUserStatus(
  userId: string,
  status: "APPROVED" | "REVOKED"
): Promise<User> {
  return api.patch(`/admin/users/${userId}/status`, { status });
}

export async function updateUserQuota(
  userId: string,
  quotaBytes: string
): Promise<User> {
  return api.patch(`/admin/users/${userId}/quota`, { quotaBytes });
}

export async function recalculateUsage(
  userId: string
): Promise<{ usedBytes: string }> {
  return api.post(`/admin/users/${userId}/recalculate`);
}

export async function getSystemStats(): Promise<{
  approvedUsers: number;
  totalUsedBytes: string;
  totalQuotaBytes: string;
}> {
  return api.get("/admin/stats");
}

/** Read-only admin browse of another user's folders/files */
export async function adminListExplorerFolders(
  userId: string,
  parentId: string | null
): Promise<FolderDTO[]> {
  const qs = new URLSearchParams();
  if (parentId) qs.set("parentId", parentId);
  const q = qs.toString();
  return api.get(`/admin/users/${userId}/explorer/folders${q ? `?${q}` : ""}`);
}

export async function adminListExplorerFiles(
  userId: string,
  params: {
    folderId?: string | null;
    sort?: SortField;
    order?: SortOrder;
    limit?: number;
    cursor?: string | null;
  } = {}
): Promise<{ files: FileDTO[]; total: number; nextCursor: string | null }> {
  const qs = new URLSearchParams();
  if (params.folderId) qs.set("folderId", params.folderId);
  if (params.sort) qs.set("sort", params.sort);
  if (params.order) qs.set("order", params.order);
  if (params.limit != null) qs.set("limit", String(params.limit));
  if (params.cursor) qs.set("cursor", params.cursor);
  return api.get(`/admin/users/${userId}/explorer/files?${qs.toString()}`);
}

export async function adminGetExplorerFolderTree(
  userId: string,
  folderId: string
): Promise<Array<{ id: string; name: string; path: string }>> {
  return api.get(`/admin/users/${userId}/explorer/folders/${folderId}/tree`);
}

export async function getAdminExplorerDownloadUrl(
  userId: string,
  fileId: string,
  signal?: AbortSignal
): Promise<{ url: string }> {
  return api.get(`/admin/users/${userId}/explorer/files/${fileId}/download-url`, signal ? { signal } : {});
}

/** Batch-fetches download URLs for several files in one request — used to prefetch preview neighbors. */
export async function getAdminExplorerDownloadUrls(
  userId: string,
  fileIds: string[],
  signal?: AbortSignal
): Promise<{ urls: Record<string, string> }> {
  return api.post(`/admin/users/${userId}/explorer/files/download-urls`, { fileIds }, signal ? { signal } : {});
}

export async function getAdminExplorerThumbnailUrl(
  userId: string,
  fileId: string
): Promise<{ url: string | null }> {
  return api.get(`/admin/users/${userId}/explorer/files/${fileId}/thumbnail-url`);
}

export async function downloadAdminExplorerFile(
  userId: string,
  fileId: string,
  fileName: string
): Promise<void> {
  const { url } = await getAdminExplorerDownloadUrl(userId, fileId);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
}

export interface DeletedFileRecord {
  id: string;
  name: string;
  mimeType: string;
  extension: string | null;
  sizeBytes: string;
  s3Key: string;
  userId: string;
  deletedAt: string | null;
  permanentlyDeletedAt: string | null;
  user: { email: string; displayName: string | null };
}

export async function listAdminDeletedFiles(opts: {
  userId?: string;
  page: number;
  limit: number;
}): Promise<{ files: DeletedFileRecord[]; total: number; page: number; limit: number }> {
  const qs = new URLSearchParams();
  if (opts.userId) qs.set("userId", opts.userId);
  qs.set("page", String(opts.page));
  qs.set("limit", String(opts.limit));
  return api.get(`/admin/deleted-files?${qs.toString()}`);
}

export async function getAdminDeletedFileDownloadUrl(fileId: string): Promise<{ url: string }> {
  return api.get(`/admin/deleted-files/${fileId}/download-url`);
}

export async function adminTrueDeleteFile(fileId: string): Promise<void> {
  return api.delete(`/admin/deleted-files/${fileId}`);
}
